import "./file-organizer-form.css";
import { RectangleStackIcon } from "@heroicons/react/24/outline";
import { GenericError } from "@via/components/error.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { Sample } from "@via/context/file-organizer-context.tsx";
import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import {
  fetchDatasetNameExist,
  useCollectionList,
  useCreateCollection,
} from "@via/pages/data/data-collections/data-collections.api.tsx";
import { ERROR } from "@via/utils/constants.tsx";
import {
  fileOrganizerFormOptions,
  requiredValidation,
} from "@via/utils/form-validators.tsx";
import {
  DatasetListResponseItem,
  GeoItem,
  GoogleCredentialUsageScope,
} from "common";
import { type JSX, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  useGetSavedAmazonCredentialsList,
  useGetSavedGoogleCredentialsList,
} from "@via/pages/data/data-sources/data-source-credentials.api.tsx";
import { useAuth } from "@via/hooks/useAuth";
import {
  MembershipConnectionsWarning,
  useMembershipGoogleConnections,
} from "@via/pages/organization/membership-connections.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { classNames } from "@via/utils/helpers.tsx";
import CollapsibleContainer from "@via/components/collapsible-container/collapsible-container.tsx";
import { HeadingSize } from "@via/components/page-heading.tsx";
import { useDebounceCallback } from "usehooks-ts";
import { SelectOption } from "@via/utils/interfaces";
import {
  BUILT_IN_ACCESS_OPTION_ID,
  BUILT_IN_ACCESS_OPTION_LABEL,
} from "@via/pages/data/constants.ts";

export interface IFileOrganizerForm {
  newCollectionName?: string;
  collectionId: string;
  archiveDir?: string;
  s3ArchiveDir?: string;
  s3ArchiveCredentialId?: string | number;
  gsArchiveDir?: string;
  gsArchiveCredentialId?: string | number;
}

export default function FileOrganizerForm({
  onFormSave,
  isLoading,
  geoList,
}: {
  onFormSave: (payload: IFileOrganizerForm) => void;
  isLoading: boolean;
  geoList?: GeoItem[];
}): JSX.Element {
  const [searchParams] = useSearchParams();
  const { selectedCollectionId: contextCollectionId } = useGlobal();
  const { user } = useAuth();

  // Read datasetId from URL params, fallback to context
  const selectedCollectionId =
    searchParams.get("datasetId") || contextCollectionId;
  const [page, setPage] = useState(0);
  const pageSize = 10;
  const [searchInput, setSearchInput] = useState("");
  const [cumulativeCollections, setCumulativeCollections] = useState<
    DatasetListResponseItem[]
  >([]);
  const [missingCollections, setMissingCollections] = useState<
    DatasetListResponseItem[]
  >([]);
  const [createNewDataset, setCreateNewDataset] = useState(true);

  let selectedSamples: Sample[] | GeoItem[] = [];
  if (Array.isArray(geoList)) {
    selectedSamples = [...geoList];
  } else {
    selectedSamples = useFileOrganizer().selectedSamples || [];
  }

  const findCollectionById = (
    allCollections: SelectOption[],
    collectionId: string
  ): string => {
    return (
      allCollections?.find(
        collection => collection.value === Number(collectionId)
      )?.label || ""
    );
  };

  const {
    isLoading: isCollectionsLoading,
    isError: isCollectionsError,
    data: collections,
  } = useCollectionList(
    page,
    pageSize,
    [],
    [{ id: "name", value: searchInput }]
  );

  // Fetch missing selected collections separately
  const {
    data: missingCollectionsData,
    isLoading: isMissingCollectionsDataLoading,
  } = useCollectionList(
    0,
    1,
    [],
    [{ id: "id", value: selectedCollectionId ?? "" }],
    selectedCollectionId ? true : false // only fetch if there is a selected collection
  );

  useEffect(() => {
    if (missingCollectionsData?.data) {
      setMissingCollections(missingCollectionsData.data);
    }
  }, [missingCollectionsData]);

  const fetchMoreCollections = useCallback(() => {
    setPage(currentPage => {
      const nextPage = currentPage + 1;
      const maxPage = Math.ceil((collections?.total ?? 0) / pageSize) - 1;
      return nextPage <= maxPage ? nextPage : currentPage;
    });
  }, [collections?.total]);

  const debouncedHandleInputChange = useDebounceCallback((value: string) => {
    if (value === searchInput) return;
    setPage(0);
    setSearchInput(value);
  }, 300);

  const {
    mutateAsync: createCollection,
    isPending: isCreateCollectionLoading,
  } = useCreateCollection();

  useEffect(() => {
    if (collections?.data) {
      setCumulativeCollections(prevCollections => {
        // Append new results while preventing duplicates
        const existingIds = new Set(prevCollections.map(p => p.id));
        const newCollections = collections?.data?.filter(
          p => !existingIds.has(p.id)
        );

        return [...prevCollections, ...newCollections];
      });
    }
  }, [collections?.data, searchInput, page]);

  const isError = isCollectionsError;

  const datasetOptionLabels = useMemo(() => {
    const prepareDatasetLabel = (
      name: string,
      datasetType: string,
      fileType: string
    ): string => {
      if (datasetType && fileType) {
        return name + " (" + datasetType + "/" + fileType + ")";
      } else if (datasetType) {
        return name + " (" + datasetType + ")";
      } else if (fileType) {
        return name + " (" + fileType + ")";
      } else {
        return name;
      }
    };

    const map = new Map<number, DatasetListResponseItem>();
    cumulativeCollections.forEach(collection =>
      map.set(collection.id, collection)
    );
    missingCollections.forEach(collection =>
      map.set(collection.id, collection)
    );
    const mergedCollections = Array.from(map.values());

    return (
      mergedCollections?.map(({ name, id, datasetType, fileType }) => ({
        label: prepareDatasetLabel(name, datasetType, fileType),
        value: id,
      })) ?? []
    );
  }, [cumulativeCollections, missingCollections]);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<IFileOrganizerForm>({
    mode: "onChange",
    shouldUnregister: true,
  });

  const s3ArchiveDirValue = watch("s3ArchiveDir");
  const {
    isLoading: isSavedAmazonCredentialsLoading,
    data: savedAmazonCredentials = [],
  } = useGetSavedAmazonCredentialsList();
  const amazonCredentialList = [
    { name: BUILT_IN_ACCESS_OPTION_LABEL, id: BUILT_IN_ACCESS_OPTION_ID },
    ...savedAmazonCredentials,
  ];

  const gsArchiveDirValue = watch("gsArchiveDir");
  const {
    isLoading: isSavedGoogleCredentialsLoading,
    data: savedGoogleCredentials = [],
  } = useGetSavedGoogleCredentialsList();
  // Every active membership's saved connections, labeled by organization (#1135).
  const {
    connections: orgGoogleCredentials,
    failedOrganizationNames: failedGoogleOrganizations,
  } = useMembershipGoogleConnections(user, {
    usageScope: GoogleCredentialUsageScope.Dataset,
  });
  const googleCredentialList = [
    ...orgGoogleCredentials.map(c => ({
      name: c.label,
      id: c.id,
    })),
    { name: BUILT_IN_ACCESS_OPTION_LABEL, id: BUILT_IN_ACCESS_OPTION_ID },
    ...savedGoogleCredentials,
  ];
  const s3ArchiveCredentialId = watch("s3ArchiveCredentialId");
  const gsArchiveCredentialId = watch("gsArchiveCredentialId");

  useEffect(() => {
    if (selectedCollectionId && collections) {
      setValue("collectionId", selectedCollectionId);
    }
  }, [selectedCollectionId, collections]);

  useEffect(() => {
    // if s3ArchiveDirValue is present, automatically set s3ArchiveCredentialId to BUILT_IN_ACCESS_OPTION_ID
    if (s3ArchiveDirValue && !s3ArchiveCredentialId) {
      setValue("s3ArchiveCredentialId", BUILT_IN_ACCESS_OPTION_ID);
    } else if (!s3ArchiveDirValue && s3ArchiveCredentialId) {
      setValue("s3ArchiveCredentialId", undefined);
    }
  }, [s3ArchiveDirValue, s3ArchiveCredentialId, setValue]);

  useEffect(() => {
    // if gsArchiveDirValue is present, automatically set gsArchiveCredentialId to BUILT_IN_ACCESS_OPTION_ID
    if (gsArchiveDirValue && !gsArchiveCredentialId) {
      setValue("gsArchiveCredentialId", BUILT_IN_ACCESS_OPTION_ID);
    } else if (!gsArchiveDirValue && gsArchiveCredentialId) {
      setValue("gsArchiveCredentialId", undefined);
    }
  }, [gsArchiveDirValue, gsArchiveCredentialId, setValue]);

  const handleFileOrganizerFormSubmit = async (
    payload: IFileOrganizerForm
  ): Promise<void> => {
    if (selectedSamples?.length) {
      if (createNewDataset && payload?.newCollectionName) {
        const collectionCreationResult = await handleCreateOption(
          payload.newCollectionName
        );
        delete payload.newCollectionName;
        onFormSave({ ...payload, collectionId: collectionCreationResult });
      } else {
        delete payload.newCollectionName;
        onFormSave(payload);
      }
    }
  };

  const handleCreateOption = async (
    collectionName: string
  ): Promise<string> => {
    const result = await createCollection({ name: collectionName });
    if (result) {
      setValue("collectionId", result.id);
    }

    return result.id;
  };

  const debouncedValidateDatasetName = useDebounceCallback(
    async (value: string, callback: (result: boolean) => void) => {
      const res = await fetchDatasetNameExist({ id: "name", value });
      if (res?.total > 0) {
        callback(false);
      }
      callback(true);
    },
    300
  );

  const validateDatasetName = async (value: string): Promise<string | true> => {
    if (!value) {
      return "Dataset name is required";
    }

    return new Promise(resolve => {
      debouncedValidateDatasetName(value, exists => {
        if (!exists) {
          resolve(`Dataset name: "${value}" already exists`);
        } else {
          resolve(true);
        }
      });
    });
  };

  return (
    <div>
      <form
        onSubmit={e => {
          e.preventDefault();
          handleSubmit(handleFileOrganizerFormSubmit)(e);
        }}>
        {isError ? (
          <div className="via-generic-error">
            <GenericError message={ERROR} />
          </div>
        ) : (
          <div className="pt-6] space-y-6 pb-5">
            {selectedCollectionId ? (
              <div className="flex flex-col text-sm leading-6 text-slate-600">
                <label className="block text-sm font-medium leading-6 text-gray-900">
                  Selected Dataset
                </label>
                <div className="mt-2">
                  <input
                    data-testid="preSelectedCollectionInput"
                    type="text"
                    value={findCollectionById(
                      datasetOptionLabels,
                      selectedCollectionId
                    )}
                    disabled={true}
                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            ) : (
              <div className="flex flex-col text-sm leading-6 text-slate-600">
                <div className="flex gap-4">
                  <label
                    className={classNames(
                      !createNewDataset ? "opacity-50" : "",
                      "flex items-center space-x-2"
                    )}>
                    <input
                      type="radio"
                      value="create"
                      checked={createNewDataset}
                      onChange={() => setCreateNewDataset(true)}
                    />
                    <span>Create New Dataset</span>
                  </label>

                  <label
                    className={classNames(
                      createNewDataset ? "opacity-50" : "",
                      "flex items-center space-x-2"
                    )}>
                    <input
                      type="radio"
                      value="select"
                      checked={!createNewDataset}
                      onChange={() => setCreateNewDataset(false)}
                    />
                    <span>Select Existing Dataset</span>
                  </label>
                </div>

                {createNewDataset ? (
                  <FormInputComponent
                    control={control}
                    name={"newCollectionName"}
                    placeholder={"Enter Your Dataset Name"}
                    validations={{
                      ...requiredValidation("Dataset"),
                      validate: validateDatasetName,
                    }}
                    disabled={
                      isCollectionsLoading || isMissingCollectionsDataLoading
                    }
                  />
                ) : (
                  <div className="mt-2" data-testid="collectionSelector">
                    <SelectComponent
                      control={control}
                      name="collectionId"
                      rules={fileOrganizerFormOptions.collectionId}
                      options={datasetOptionLabels}
                      isLoading={
                        isCollectionsLoading ||
                        isCreateCollectionLoading ||
                        isMissingCollectionsDataLoading
                      }
                      placeholder="Choose or Create Dataset"
                      prefixIcon={<RectangleStackIcon />}
                      isCreatable={false}
                      noOptionsMessage="No results available, create a new one"
                      onFetchMoreOptions={fetchMoreCollections}
                      onInputChange={inputValue =>
                        debouncedHandleInputChange(inputValue)
                      }
                    />
                    {errors.collectionId && (
                      <span className="text-xs text-rose-500">
                        {errors.collectionId.message}
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="collapsible-container-wrapper">
              <CollapsibleContainer
                title={"Backup to Another Location (optional)"}
                size={HeadingSize.XSmall}>
                <div
                  className="advanced-options"
                  style={{ transition: "width 0.2s ease" }}>
                  <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                    Local Archive Directory (optional)
                  </label>
                  <div className="mt-2">
                    <input
                      data-testid="localArchiveInput"
                      {...register("archiveDir")}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter your local archive directory"
                    />
                  </div>

                  <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                    Amazon S3 Backup (optional)
                  </label>
                  <div className="mt-2">
                    <input
                      data-testid="awsBackupInput"
                      {...register("s3ArchiveDir")}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter your Amazon S3 Backup"
                    />
                  </div>

                  {s3ArchiveDirValue && (
                    <div>
                      <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                        Amazon S3 Backup Credentials
                      </label>
                      <div
                        className="mt-2"
                        data-testid="s3ArchiveCredentialIdSelector">
                        <SelectComponent
                          control={control}
                          name="s3ArchiveCredentialId"
                          options={
                            amazonCredentialList?.map(({ name, id }) => ({
                              label: name,
                              value: id,
                            })) ?? []
                          }
                          isLoading={isSavedAmazonCredentialsLoading}
                          placeholder="Choose Amazon Backup Credentials"
                        />
                      </div>
                    </div>
                  )}

                  <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                    Google Storage Backup (optional)
                  </label>
                  <div className="mt-2">
                    <input
                      data-testid="gcsBackupInput"
                      {...register("gsArchiveDir")}
                      type="text"
                      className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                      placeholder="Enter your Google Storage Backup"
                    />
                  </div>

                  {gsArchiveDirValue && (
                    <div>
                      <label className="mt-4 block text-sm font-medium leading-6 text-gray-900">
                        Google Cloud Backup Credentials
                      </label>
                      <div
                        className="mt-2"
                        data-testid="gsArchiveCredentialIdSelector">
                        <SelectComponent
                          control={control}
                          name="gsArchiveCredentialId"
                          options={
                            googleCredentialList?.map(({ name, id }) => ({
                              label: name,
                              value: id,
                            })) ?? []
                          }
                          isLoading={isSavedGoogleCredentialsLoading}
                          placeholder="Choose Google Cloud Backup Credentials"
                        />
                        <MembershipConnectionsWarning
                          names={failedGoogleOrganizations}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </CollapsibleContainer>
            </div>

            <div className="flex flex-shrink-0 justify-end py-4">
              <button
                disabled={isLoading || isCreateCollectionLoading}
                data-testid="saveFilesToCollectionButton"
                type="submit"
                className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
                <Spinner loading={isLoading || isCreateCollectionLoading}>
                  Save Files to Dataset
                </Spinner>
              </button>
            </div>

            {selectedSamples?.length ? null : (
              <div className="text-s flex flex-shrink-0 justify-end text-rose-500">
                Add files before saving
              </div>
            )}
          </div>
        )}
      </form>
    </div>
  );
}
